import { usersStore } from '@/src/store/users';
import { storeToRefs } from 'pinia';
import { v4 as uuidv4 } from 'uuid';
import { useStorage } from './useStorage';

type KlaviyoEventProperties = {
  [key: string]: string | number | boolean | Array<any>;
};

export function useKlaviyo() {
  const { getStorageValue, setStorageValue } = useStorage();

  // Method added to ensure that the session ID is always set and unique, even if the user is not logged in
  // So we can track events and lately link events to a user
  const upsertSessionId = () => {
    let sessionId = null;

    sessionId = getStorageValue('klaviyo_session_id');

    if (!sessionId) {
      sessionId = uuidv4();
      setStorageValue('klaviyo_session_id', sessionId);
    }

    return sessionId;
  };

  // We need to call identify method for logged in users, but since there is no clear explanation for how long identity preserved, we will call it once per day as,for every live browser's session.
  const identifyLoggedInUser = (cb: () => void) => {
    const lastSessionIdentifyingTime = sessionStorage.getItem('last_session_identifying_time');

    const now = new Date().getTime();
    const oneDayInMilliseconds = 1000 * 60 * 60 * 24;

    if (!lastSessionIdentifyingTime) {
      sessionStorage.setItem('last_session_identifying_time', String(now + oneDayInMilliseconds));

      cb();
      return;
    }

    const expiringTime = parseInt(lastSessionIdentifyingTime);

    if (now > expiringTime) {
      sessionStorage.setItem('last_session_identifying_time', String(now + oneDayInMilliseconds));
      cb();
      return;
    }
  };

  const trackEvent = ({ eventName, properties }: { eventName: string; properties: KlaviyoEventProperties }) => {
    const { currentUser } = storeToRefs(usersStore());

    const klaviyoInstance = window.klaviyo;

    if (!klaviyoInstance) {
      console.log('Klaviyo is not initialized');
      return false;
    }

    const klaviyoSessionId = upsertSessionId();

    const userData = currentUser.value;

    if (userData.email) {
      identifyLoggedInUser(() => {
        klaviyoInstance.identify({ email: userData.email });
      });
    }

    klaviyoInstance.track(eventName, {
      session_id: klaviyoSessionId,
      time: new Date().toISOString(),
      ...properties,
    });

    return true;
  };

  const trackSpecificEvent = (eventName: string) => (eventData: KlaviyoEventProperties) => {
    if (Object.keys(eventData).length === 0) return;

    return trackEvent({
      eventName,
      properties: {
        ...eventData,
      },
    });
  };

  const trackAdvisorFeeUpdates = (changes: KlaviyoEventProperties = {}) => {
    const { currentUser } = storeToRefs(usersStore());
    const fnToCall = Object.keys(changes)
      .filter((changeName) => Boolean(changes[changeName]))
      .map((changedFeeName) => {
        const currentValue = parseFloat(currentUser.value[changedFeeName as keyof typeof currentUser.value]);
        const newValue = parseFloat(changes[changedFeeName] as string);
        const feeType = changedFeeName.split('_').pop();
        if (newValue < currentValue) {
          return () =>
            trackEvent({
              eventName: `Price Drop Alert: ${feeType}`,
              properties: {
                advisor_id: currentUser.value.id,
                advisor_name: currentUser.value.name,
                old_fee: currentValue,
                new_fee: newValue,
              },
            });
        }
        return () => null;
      });

    fnToCall.forEach((fn) => fn());
  };

  const buildKlaviyoConnectionString = () => {
    if (!import.meta.env.VITE_KLAVIYO_PUBLIC_KEY) return '';
    return `<script async type="text/javascript" src="https://static.klaviyo.com/onsite/js/${
      import.meta.env.VITE_KLAVIYO_PUBLIC_KEY
    }/klaviyo.js"></script>`;
  };

  return {
    buildKlaviyoConnectionString,
    trackSpecificEvent,
    trackAdvisorFeeUpdates,
  };
}
